import { useState } from 'react';
import type { ChangeEvent, FC, FormEvent } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import './EmailVerificationPage.css';

interface RegistrationData {
  email: string;
  emailVerificationCode?: string;
}

interface ApiResponse {
  success: boolean;
  message?: string;
}

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL ?? '');

function isRegistrationData(value: unknown): value is RegistrationData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'email' in value &&
    typeof (value as { email: unknown }).email === 'string'
  );
}

function normalizeRegistrationData(value: RegistrationData): RegistrationData {
  const candidate = value as RegistrationData & { emailVerificationCode?: number | string };
  const fallbackCode = candidate.emailVerificationCode;
  return {
    email: candidate.email,
    emailVerificationCode:
      typeof fallbackCode === 'number' || typeof fallbackCode === 'string'
        ? String(fallbackCode)
        : undefined,
  };
}

export const EmailVerificationPage: FC = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();

  // Safely extract registrationData from location.state
  const rawState: unknown = location.state;
  let registrationData: RegistrationData | null = null;
  if (rawState && typeof rawState === 'object' && 'registrationData' in rawState) {
    const maybe = (rawState as { registrationData: unknown }).registrationData;
    if (isRegistrationData(maybe)) {
      registrationData = normalizeRegistrationData(maybe);
    }
  }
  const [verificationCode, setVerificationCode] = useState<string>(
    registrationData?.emailVerificationCode ?? '',
  );
  const [error, setError] = useState<string>('');

  const handleVerify = async (event_: FormEvent<HTMLFormElement>): Promise<void> => {
    event_.preventDefault();

    if (!registrationData) {
      setError(t('verify.missingData'));
      return;
    }

    try {
      const response = await axios.post<ApiResponse>(`${API_BASE_URL}/api/auth/verify-email`, {
        email: registrationData.email,
        verificationCode,
      });

      if (response.data.success) {
        navigate('/home');
      } else {
        setError(response.data.message ?? t('verify.invalidCode'));
      }
    } catch {
      setError(t('verify.genericError'));
    }
  };

  return (
    <div className="email-verification-container">
      <h2>{t('verify.emailTitle')}</h2>
      <p>
        {t('verify.enterCodeTo')} {registrationData?.email ?? ''}
      </p>
      <form onSubmit={handleVerify}>
        <input
          type="text"
          placeholder={t('verify.codePlaceholder')}
          value={verificationCode}
          onChange={(event_: ChangeEvent<HTMLInputElement>) =>
            setVerificationCode(event_.target.value)
          }
        />
        <button type="submit">{t('verify.verify')}</button>
      </form>
      {error && <p className="error">{error}</p>}
    </div>
  );
};
